/**
 * Single source of truth for donista's store/install URLs and the logic that
 * picks the right one for a given client. Previously these URLs were duplicated
 * across app-promo, the donation-reminder notification, and structured-data.
 *
 * Copy (labels/prefixes) intentionally lives in the i18n catalogs, not here —
 * this module only owns URLs + the selection logic.
 */

export const STORE_URLS = {
  appStore: "https://apps.apple.com/de/app/donista-org/id6739247332",
  googlePlay: "https://play.google.com/store/apps/details?id=org.donista.donista",
  chromeWebStore:
    "https://chromewebstore.google.com/detail/donista-automatic-donatio/jmjionpdmejlolhikpnpdagbpclcppfh",
  firefoxAddons:
    "https://addons.mozilla.org/de/firefox/addon/donista-automatic-donations/",
} as const

/** Device class, as returned by `useDevice()`. */
export type Device = "ios" | "android" | "desktop"
/** Browser class, as returned by `useBrowser()`. */
export type Browser = "chrome" | "firefox" | "safari"

export type InstallKind = "appStore" | "googlePlay" | "chromeWebStore" | "firefoxAddons"

export interface InstallTarget {
  kind: InstallKind
  url: string
}

/**
 * Resolve the best install target for a client. Device wins: on mobile we always
 * point at the app store (the browser is irrelevant there, and this also covers
 * in-app webviews). Only on desktop does the browser decide which extension store.
 */
export function resolveInstallTarget({
  device,
  browser,
}: {
  device: Device
  browser: Browser
}): InstallTarget {
  if (device === "ios") return { kind: "appStore", url: STORE_URLS.appStore }
  if (device === "android") return { kind: "googlePlay", url: STORE_URLS.googlePlay }

  // desktop → extension store by browser
  if (browser === "firefox") return { kind: "firefoxAddons", url: STORE_URLS.firefoxAddons }
  if (browser === "safari") return { kind: "appStore", url: STORE_URLS.appStore }
  return { kind: "chromeWebStore", url: STORE_URLS.chromeWebStore }
}
